import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import snakify from "snakify-ts";
import {
  CustomerBodySchema,
  CustomerSchema,
  DefaultService,
} from "../generated";
import camelize, { Camelize } from "camelize-ts";
import { toaster } from "../components/toaster";

export const useCustomerSearch = (searchQuery: string, size?: number) => {
  return useQuery({
    queryKey: ["customerSearch", searchQuery],
    queryFn: () =>
      DefaultService.fakturCustomersViewsCustomerSearch({
        query: searchQuery,
        size,
      }),
    select: (data) => data.map((customer) => camelize(customer)),
  });
};

export const useCustomer = (customerNumber: number | null) => {
  return useQuery({
    queryKey: ["customer", customerNumber],
    queryFn: () =>
      DefaultService.fakturCustomersViewsCustomer({
        customerNumber: customerNumber || 0,
      }),
    select: (customer) => camelize(customer),
    enabled: Boolean(customerNumber),
  });
};

export const useCustomerDelete = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["customerDelete"],
    mutationFn: ({ customerNumber }: { customerNumber: number }) =>
      DefaultService.fakturCustomersViewsCustomerDelete({ customerNumber }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer"] });
      queryClient.invalidateQueries({ queryKey: ["customerSearch"] });
      queryClient.invalidateQueries({ queryKey: ["customerList"] });
      navigate("/customers/");
    },
  });
};

export const useCustomerList = () => {
  return useQuery({
    queryKey: ["customerList"],
    queryFn: DefaultService.fakturCustomersViewsCustomerList,
    select: (data) => camelize(data),
  });
};

export const useCustomerCreate = ({
  onSuccessCallback,
}: {
  onSuccessCallback?: (data: Camelize<CustomerSchema>) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["customerCreate"],
    mutationFn: (data: Camelize<CustomerBodySchema>) =>
      DefaultService.fakturCustomersViewsCustomerCreate({
        requestBody: snakify(data),
      }),
    onSuccess: (data) => {
      onSuccessCallback && onSuccessCallback(camelize(data));
      queryClient.invalidateQueries({ queryKey: ["customerSearch"] });
      queryClient.invalidateQueries({ queryKey: ["customerList"] });
      toaster.success({ title: "Kunde opprettet!" });
    },
  });
};
